// src/ui/Resizer.jsx — a draggable divider that writes a CSS variable.
//
// It deliberately does NOT own the layout: it sets one custom property on a target
// element and lets CSS grid do the rest. That's what makes it safe to drop between two
// panes that already have their own scrollers, sticky headers and ResizeObservers —
// nothing is measured in JS, nothing is positioned in JS, so nothing can drift out of
// step with the real layout.
//
// Pointer events (not mouse) so a trackpad, a pen and a touchscreen all work, with
// setPointerCapture so the drag survives the cursor outracing the handle — the classic
// splitter bug where you drag fast, leave the 6px strip, and the divider stops following.

import React, { useCallback, useRef } from 'react';

export default function Resizer({
  targetRef, measureRef, cssVar, storageKey, axis = 'x',
  min = 240, max = 640, unit = 'px', fromRight = false, vertical = 'stretch',
}) {
  const dragging = useRef(false);

  const apply = useCallback((clientX) => {
    const host = targetRef?.current;
    // 🟢 the box we measure against isn't always the element we write to: the split
    //    divider is positioned inside `.chat-split` but its variable lives on the shell,
    //    which is the element that survives the split being torn down and rebuilt.
    const scope = (measureRef?.current) || host;
    if (!host || !scope) return;
    const box = scope.getBoundingClientRect();
    let v = fromRight ? box.right - clientX : clientX - box.left;
    // clamp in px first, then convert — so a % target still can't collapse a pane
    const lo = typeof min === 'function' ? min(box) : min;
    // 🟢 a hi below lo would let the clamp collapse the far pane to nothing (lo wins),
    //    so the two bounds are reconciled before they're applied rather than after
    const hi = Math.max(lo, typeof max === 'function' ? max(box) : max);
    v = Math.max(lo, Math.min(hi, v));
    host.style.setProperty(cssVar, unit === '%' ? `${(v / box.width) * 100}%` : `${Math.round(v)}px`);
  }, [targetRef, measureRef, cssVar, min, max, unit, fromRight]);

  const onDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    // 🟢 while dragging, the whole document adopts the resize cursor and stops selecting
    //    text — otherwise sweeping across the message list highlights half the transcript
    document.body.style.cursor = axis === 'x' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    apply(e.clientX);
  };
  const onMove = (e) => { if (dragging.current) apply(e.clientX); };
  const end = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    if (storageKey) {
      const v = targetRef?.current?.style.getPropertyValue(cssVar);
      if (v) { try { localStorage.setItem(storageKey, v); } catch {} }
    }
  };

  // double-click resets to whatever the stylesheet says — the standard escape hatch
  // for a splitter you've dragged somewhere silly
  const reset = () => {
    targetRef?.current?.style.removeProperty(cssVar);
    if (storageKey) { try { localStorage.removeItem(storageKey); } catch {} }
  };

  return (
    <div
      className={`rsz ${vertical}`}
      role="separator"
      aria-orientation="vertical"
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={end}
      onPointerCancel={end}
      onDoubleClick={reset}
      title="Drag to resize · double-click to reset"
    >
      <span className="rsz-line" />
    </div>
  );
}

/** Re-apply a persisted size before the first paint. */
export function restoreSize(el, cssVar, storageKey) {
  if (!el || !storageKey) return;
  try {
    const v = localStorage.getItem(storageKey);
    if (v) el.style.setProperty(cssVar, v);
  } catch {}
}
